import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LuCheck, LuChevronDown } from 'react-icons/lu';

const flattenOptions = (children) => {
  const out = [];
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === React.Fragment || child.type === 'optgroup') {
      out.push(...flattenOptions(child.props.children));
      return;
    }
    if (child.type === 'option') {
      const { value, children: label, disabled } = child.props;
      const text = React.Children.toArray(label).join('');
      out.push({
        value: value === undefined || value === null ? text : String(value),
        label: text,
        disabled: Boolean(disabled),
      });
    }
  });
  return out;
};

const makeEvent = (name, value) => {
  const target = { name, value };
  return {
    target,
    currentTarget: target,
    type: 'change',
    persist() {},
    preventDefault() {},
    stopPropagation() {},
  };
};

const MENU_MAX_HEIGHT = 300;

/**
 * Accessible drop-in replacement for a native <select>.
 * Accepts the same <option> children and calls onChange with an event-like
 * object ({ target: { name, value } }) so existing handlers keep working.
 *
 * portal: render the listbox in document.body with viewport-aware placement
 * (use inside cards, tables and toolbars that clip overflow).
 */
const Select = ({
  id,
  name,
  value,
  onChange,
  disabled = false,
  invalid = false,
  className = '',
  classPrefix = 'rt-select',
  size = 'md',
  portal = true,
  placeholder,
  title,
  children,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
}) => {
  const p = classPrefix;
  const autoId = useId();
  const baseId = id || `${p}-${autoId.replace(/:/g, '')}`;
  const listId = `${baseId}-list`;
  const options = useMemo(() => flattenOptions(children), [children]);
  const current = value === undefined || value === null ? '' : String(value);
  const selectedIndex = options.findIndex((o) => o.value === current);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : null;

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [placeUp, setPlaceUp] = useState(false);
  const [coords, setCoords] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const typeahead = useRef({ text: '', timer: null });

  const firstEnabled = useCallback(
    (from = 0, step = 1) => {
      const n = options.length;
      if (!n) return -1;
      for (let i = 0; i < n; i += 1) {
        const idx = (from + i * step + n * n) % n;
        if (!options[idx].disabled) return idx;
      }
      return -1;
    },
    [options]
  );

  const measure = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const below = window.innerHeight - rect.bottom;
    const up = below < Math.min(MENU_MAX_HEIGHT, options.length * 36 + 12) + 12 && rect.top > below;
    setPlaceUp(up);
    if (portal) {
      const width = Math.max(rect.width, 180);
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
      setCoords({
        left,
        width,
        top: up ? 'auto' : rect.bottom + 4,
        bottom: up ? window.innerHeight - rect.top + 4 : 'auto',
        maxHeight: Math.max(140, Math.min(MENU_MAX_HEIGHT, (up ? rect.top : below) - 16)),
      });
    }
  }, [portal, options.length]);

  const openMenu = useCallback(
    (preferIndex) => {
      if (disabled) return;
      measure();
      const start =
        preferIndex !== undefined
          ? preferIndex
          : selectedIndex >= 0 && !options[selectedIndex]?.disabled
          ? selectedIndex
          : firstEnabled(0, 1);
      setActiveIndex(start);
      setOpen(true);
    },
    [disabled, selectedIndex, options, firstEnabled, measure]
  );

  const closeMenu = useCallback((refocus = true) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus({ preventScroll: true });
  }, []);

  const commit = useCallback(
    (index) => {
      const opt = options[index];
      if (!opt || opt.disabled) return;
      if (opt.value !== current && typeof onChange === 'function') {
        onChange(makeEvent(name, opt.value));
      }
      closeMenu();
    },
    [options, current, onChange, name, closeMenu]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e) => {
      if (rootRef.current?.contains(e.target) || listRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onReflow = (e) => {
      if (listRef.current && e?.target && listRef.current.contains(e.target)) return;
      measure();
    };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('touchstart', onDocDown);
    window.addEventListener('resize', onReflow);
    if (portal) window.addEventListener('scroll', onReflow, true);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('touchstart', onDocDown);
      window.removeEventListener('resize', onReflow);
      if (portal) window.removeEventListener('scroll', onReflow, true);
    };
  }, [open, portal, measure]);

  useLayoutEffect(() => {
    if (!open || activeIndex < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-index="${activeIndex}"]`);
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  useEffect(() => {
    if (disabled && open) setOpen(false);
  }, [disabled, open]);

  useEffect(() => () => clearTimeout(typeahead.current.timer), []);

  const moveActive = (step) => {
    setActiveIndex((prev) => firstEnabled((prev < 0 ? (step > 0 ? -1 : 0) : prev) + step, step));
  };

  const handleTypeahead = (char) => {
    const state = typeahead.current;
    clearTimeout(state.timer);
    state.text += char.toLowerCase();
    state.timer = setTimeout(() => {
      state.text = '';
    }, 500);
    const start = open ? activeIndex + 1 : selectedIndex + 1;
    const n = options.length;
    for (let i = 0; i < n; i += 1) {
      const idx = (start + i) % n;
      const opt = options[idx];
      if (!opt.disabled && opt.label.toLowerCase().startsWith(state.text)) {
        if (open) setActiveIndex(idx);
        else openMenu(idx);
        return;
      }
    }
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!open) openMenu();
        else moveActive(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!open) openMenu();
        else moveActive(-1);
        break;
      case 'Home':
        if (open) {
          e.preventDefault();
          setActiveIndex(firstEnabled(0, 1));
        }
        break;
      case 'End':
        if (open) {
          e.preventDefault();
          setActiveIndex(firstEnabled(options.length - 1, -1));
        }
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (!open) openMenu();
        else if (activeIndex >= 0) commit(activeIndex);
        break;
      case 'Escape':
        if (open) {
          e.preventDefault();
          e.stopPropagation();
          e.nativeEvent?.stopImmediatePropagation?.();
          closeMenu();
        }
        break;
      case 'Tab':
        if (open) setOpen(false);
        break;
      default:
        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) handleTypeahead(e.key);
    }
  };

  const isPlaceholder = !selected || selected.value === '';
  const displayLabel = selected ? selected.label : placeholder || options[0]?.label || '';
  const rootClass = [
    p,
    size === 'sm' ? `${p}--sm` : '',
    open ? 'is-open' : '',
    invalid ? 'is-invalid' : '',
    disabled ? 'is-disabled' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const menu = (
    <ul
      ref={listRef}
      id={listId}
      role="listbox"
      className={`${p}__menu ${placeUp ? 'is-up' : ''} ${portal ? `${p}__menu--portal` : ''}`}
      aria-labelledby={ariaLabelledBy || baseId}
      tabIndex={-1}
      data-rt-modal-keep-escape=""
      style={
        portal && coords
          ? {
              position: 'fixed',
              left: coords.left,
              minWidth: coords.width,
              top: coords.top,
              bottom: coords.bottom,
              maxHeight: coords.maxHeight,
            }
          : undefined
      }
    >
      {options.length === 0 && <li className={`${p}__empty`}>No options available</li>}
      {options.map((opt, index) => {
        const isSelected = opt.value === current;
        const cls = [
          `${p}__option`,
          index === activeIndex ? 'is-active' : '',
          isSelected ? 'is-selected' : '',
          opt.value === '' ? 'is-placeholder' : '',
        ]
          .filter(Boolean)
          .join(' ');
        return (
          <li
            key={`${opt.value}-${index}`}
            id={`${baseId}-opt-${index}`}
            data-index={index}
            role="option"
            aria-selected={isSelected}
            aria-disabled={opt.disabled || undefined}
            className={cls}
            onMouseEnter={() => !opt.disabled && setActiveIndex(index)}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => commit(index)}
          >
            <span>{opt.label}</span>
            {isSelected && opt.value !== '' && <LuCheck size={15} aria-hidden="true" />}
            {opt.disabled && !isSelected && <span className={`${p}__option-hint`}>Unavailable</span>}
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className={rootClass} ref={rootRef} data-rt-modal-keep-escape={open ? '' : undefined}>
      <button
        ref={triggerRef}
        id={baseId}
        type="button"
        className={`${p}__trigger`}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open && activeIndex >= 0 ? `${baseId}-opt-${activeIndex}` : undefined}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-invalid={invalid || undefined}
        title={title}
        disabled={disabled}
        onClick={() => (open ? closeMenu(false) : openMenu())}
        onKeyDown={onKeyDown}
      >
        <span className={`${p}__value ${isPlaceholder ? 'is-placeholder' : ''}`}>{displayLabel}</span>
        <LuChevronDown className={`${p}__chevron`} size={16} aria-hidden="true" />
      </button>

      {open && (portal ? createPortal(menu, document.body) : menu)}
      {name && <input type="hidden" name={name} value={current} />}
    </div>
  );
};

export default Select;
